import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import LabelChip from '../chips/LabelChip';
import type { ElementLabelChipDescriptor } from '../elementChipsTypes';
import type { TaskItemLabel } from '../../../../common/tiptap/extensions/taskList/taskListItemChipTypes';
import type { UpdateTaskItemAttrs } from '../taskItemChipsUtils';
import { ChipBaseProps } from '../../../../common/@roma/src/components/base/chip/ChipBase';
import { updateLabel } from '../../../../common/labels/labelActions';

type TaskItemLabelChipProps = {
    chip: ElementLabelChipDescriptor;
    size: ChipBaseProps['size'];
    labels: TaskItemLabel[];
    updateAttributes: UpdateTaskItemAttrs;
    canModify: boolean;
};

/**
 * Label chip for a Tiptap task item: renames update the global label, while
 * removals mutate only the owning node's label references.
 */
const TaskItemLabelChip: React.FC<TaskItemLabelChipProps> = ({ chip, size, labels, updateAttributes, canModify }) => {
    const dispatch = useDispatch();

    const onCommitEdit = useCallback(
        (labelId: string, name: string) => {
            if (!canModify) return;

            dispatch(updateLabel(labelId, { name }));
        },
        [canModify, dispatch],
    );

    const onRemove = useCallback(
        (labelId: string) => {
            if (!canModify) return;

            updateAttributes({ labels: labels.filter((label) => label.labelId !== labelId) });
        },
        [canModify, labels, updateAttributes],
    );

    const labelId = chip.label._id;

    return (
        <LabelChip
            chip={chip}
            size={size}
            canModify={canModify}
            onCommitEdit={(name) => onCommitEdit(labelId, name)}
            onRemove={() => onRemove(labelId)}
        />
    );
};

export default TaskItemLabelChip;
